import { type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';

import { BrandPaletteEditor } from '../BrandPalette/BrandPaletteEditor';

import { ONBOARDING_PATHS } from './constants/paths';
import { OnboardingLayout } from './components/OnboardingLayout';
import { useOnboarding } from './store/onboarding-store';
import { useWebBuilderAccess } from '../../hooks/useWebBuilderAccess';
import { useOnboardingTracker } from './hooks/useOnboardingTracker';
import { useBrandingDefaults } from './hooks/useBrandingDefaults';

const BrandColourStep = (): ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { primaryColor, setPrimaryColor } = useOnboarding();
  const { hasWebBuilderAccess } = useWebBuilderAccess();
  const { trackColourSaved } = useOnboardingTracker();
  // The organisation's app brand colour presets the picker when one exists. Read the preset
  // straight from the hook rather than waiting for its store write, so the editor — which
  // captures its initial colour on mount — is never mounted with an empty one.
  const { isLoading: isBrandingLoading, presetColour } = useBrandingDefaults();
  const colour = primaryColor || presetColour || '';
  const isAwaitingPreset = isBrandingLoading && !colour;

  // Upsell users came from the steps overview, so Back returns there.
  // Entitled users enter the wizard directly — there is no previous step.
  const backTo = hasWebBuilderAccess ? undefined : ONBOARDING_PATHS.steps;

  const handleContinue = () => {
    if (!colour) {
      enqueueSnackbar('Please pick a brand colour before continuing', { variant: 'error' });
      return;
    }
    trackColourSaved();
    navigate({ pathname: ONBOARDING_PATHS.logo, search: searchParams.toString() });
  };

  let paletteContent: ReactElement;
  if (isAwaitingPreset) {
    paletteContent = (
      <div className="vtwo:flex vtwo:justify-center vtwo:py-16">
        <CircularProgress size={24} />
      </div>
    );
  } else {
    paletteContent = (
      <BrandPaletteEditor
        initialColor={colour || undefined}
        websiteId="e7a0d39b-10f6-4109-a564-41e4dc75c432"
        onChange={(palette) => setPrimaryColor(palette.primary)}
      />
    );
  }

  return (
    <OnboardingLayout
      activeStep={1}
      backTo={backTo}
      primaryLabel="Continue"
      primaryDisabled={isAwaitingPreset}
      onPrimary={handleContinue}
    >
      <div className="vtwo:text-center vtwo:mb-10">
        <h1 className="vtwo:text-3xl vtwo:font-bold vtwo:mb-3">Pick your brand colour</h1>
        <p className="vtwo:text-base vtwo:text-gray-600">
          Your brand hex code, or just pick a colour from the palette. You can always update it
          later.
        </p>
      </div>

      <div className="vtwo:max-w-3xl vtwo:mx-auto vtwo:border vtwo:border-gray-200 vtwo:rounded-xl vtwo:p-8">
        {paletteContent}
      </div>
    </OnboardingLayout>
  );
};

export default BrandColourStep;
